import React from 'react';
import { SessionBundle } from '@shared/types';
import { formatPrice } from '@shared/utils';
import { getTranslatedField } from '../utils/translationUtils';
import { useI18n } from '../i18n/i18nContext';

interface SessionBundlesTableProps {
  items: SessionBundle[];
  language: string;
  onPurchase: (sessionBundle: SessionBundle) => void;
  purchaseLabel: string;
  perSessionLabel: string;
  promotionTitle?: string;
}

const SessionBundlesTable: React.FC<SessionBundlesTableProps> = ({
  items,
  language,
  onPurchase,
  purchaseLabel,
  perSessionLabel,
  promotionTitle,
}) => {
  const { t } = useI18n();
  
  const hasDiscount = items.some(item => item.originalPrice && item.originalPrice > item.price);

  const getDescription = (sessionBundle: SessionBundle): string => {
    return getTranslatedField(sessionBundle, 'shortDescription', language) as string || '';
  };

  const discountColumnLabel = promotionTitle || t.pricing.openingDealSessionBundle;

  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full table-fixed text-left text-sm whitespace-nowrap">
          <thead className="tracking-wider border-b border-blue-300 bg-blue-100">
            <tr>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 w-1/4">{t.pricing.sessionBundle}</th>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-center">{t.pricing.sessions}</th>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-center">{hasDiscount ? t.pricing.regularPrice : t.pricing.price}</th>
              {hasDiscount && (
                <th scope="col" className="px-4 py-4 font-semibold text-green-600 whitespace-normal max-w-52 text-center">{discountColumnLabel}</th>
              )}
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-center">{t.pricing.pricePerSession}</th>
              <th scope="col" className="px-4 py-4 font-semibold text-gray-700 text-right"></th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {items.map((item) => {
              const itemHasDiscount = item.originalPrice && item.originalPrice > item.price;
              const regularPrice = (itemHasDiscount ? (item.originalPrice ?? item.price) : item.price) ?? 0;
              const discountPrice = item.price;
              const pricePerSession = discountPrice / item.sessions;

              return (
                <tr key={item.id} className="border-b border-blue-200 odd:bg-white even:bg-blue-50">
                  <td className="px-4 py-4 w-1/4">
                    <div className="font-medium text-gray-900">{getTranslatedField(item, 'name', language)}</div>
                    {getDescription(item) && (
                      <div className="text-gray-700 text-sm mt-1">{getDescription(item)}</div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-gray-700 text-center">{item.sessions}</td>
                  {hasDiscount ? (
                    <>
                      <td className="px-4 py-4 text-gray-400 line-through text-center">{formatPrice(regularPrice)}</td>
                      <td className="px-4 py-4 font-bold text-gray-900 text-center">{formatPrice(discountPrice)}</td>
                    </>
                  ) : (
                    <td className="px-4 py-4 font-bold text-gray-900 text-center">{formatPrice(regularPrice)}</td>
                  )}
                  <td className="px-4 py-4 text-gray-700 text-center">{formatPrice(pricePerSession)}</td>
                  <td className="px-4 py-4 text-right">
                    <button
                      onClick={() => onPurchase(item)}
                      className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors font-medium text-sm"
                    >
                      {purchaseLabel}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden">
        {items.map((item, index) => {
          const itemHasDiscount = item.originalPrice && item.originalPrice > item.price;
          const regularPrice = (itemHasDiscount ? (item.originalPrice ?? item.price) : item.price) ?? 0;
          const discountPrice = item.price;
          const pricePerSession = discountPrice / item.sessions;

          return (
            <div
              key={item.id}
              onClick={() => onPurchase(item)}
              className={`grid grid-cols-[1.5fr_1fr_auto] gap-2 p-4 border-b border-blue-200 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'}`}
            >
              <div>
                <div className="font-medium text-gray-900">
                  {getTranslatedField(item, 'name', language)}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {getDescription(item)}
                </div>
              </div>
              <div>
                {hasDiscount ? (
                  <>
                    <div className="text-gray-400 line-through text-sm">{formatPrice(regularPrice)}</div>
                    <div className="font-bold text-gray-900">{formatPrice(discountPrice)}</div>
                  </>
                ) : (
                  <div className="font-bold text-gray-900">{formatPrice(regularPrice)}</div>
                )}
                <div className="text-xs text-gray-700 mt-1">
                  {formatPrice(pricePerSession)}/{t.pricing.pricePerSession.toLowerCase().replace('price/', '').replace('per ', '')}
                </div>
              </div>
              <div className="flex items-center justify-end">
                <button
                  onClick={(e) => { e.stopPropagation(); onPurchase(item); }}
                  className="bg-blue-600 text-white py-2 px-3 rounded-md hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  {purchaseLabel}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default SessionBundlesTable;
